import { DraftAccount } from "./draftOperations";
import {
    findDuplicateAccountNameIds,
    toScopedNameKey,
} from "./useEditorDraftValidation";

interface SaveBlockedReasonInput {
    hasBlankNames: boolean;
    hasDuplicateCodes: boolean;
    // At least one custom group has a blank or duplicate name; blocks Save the
    // same way an account name/code issue does (the server rejects both).
    hasInvalidGroupNames: boolean;
    // A pre-existing duplicate name only surfaces in the island; one this
    // session introduced blocks Save.
    hasIntroducedDuplicateNames: boolean;
}

export const buildAccountNameKeySnapshot = (
    accounts: DraftAccount[],
): Map<string, string> => {
    const snapshot = new Map<string, string>();
    for (const account of accounts) {
        const key = toScopedNameKey(account);
        if (key !== null) {
            snapshot.set(account.id, key);
        }
    }
    return snapshot;
};

export const findIntroducedDuplicateNameIds = ({
    accounts,
    seedNameKeyById,
}: {
    accounts: DraftAccount[];
    seedNameKeyById: Map<string, string>;
}): Set<string> => {
    const duplicateIds = findDuplicateAccountNameIds(accounts);
    const introduced = new Set<string>();
    for (const account of accounts) {
        if (!duplicateIds.has(account.id)) {
            continue;
        }
        const isNewOrChanged =
            seedNameKeyById.get(account.id) !== toScopedNameKey(account);
        if (isNewOrChanged) {
            introduced.add(account.id);
        }
    }
    return introduced;
};

// Single source of truth for why saving the template draft is blocked. Returns
// a single generic reason whenever any validation issue is outstanding, or
// undefined when the draft is savable. We deliberately don't spell out the
// specific issue here — the inline cell styling and the issue bar already point
// the user at what to fix. Shared by the footer's Save button and the
// close-confirmation modal.
export const resolveSaveBlockedReason = ({
    hasBlankNames,
    hasDuplicateCodes,
    hasInvalidGroupNames,
    hasIntroducedDuplicateNames,
}: SaveBlockedReasonInput): string | undefined => {
    const hasUnresolvedIssue =
        hasBlankNames ||
        hasDuplicateCodes ||
        hasInvalidGroupNames ||
        hasIntroducedDuplicateNames;

    return hasUnresolvedIssue
        ? "Some accounts have unresolved issues"
        : undefined;
};
